import { ArrowLeft, Check, ChevronRight, Copy, Link2, Loader2, Mail, Plus, RefreshCw } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { InternalOnly } from "@/app/auth/InternalOnly";
import { useAuth } from "@/modules/auth/hooks/useAuth";
import { IdentityVerificationSection } from "@/modules/verification/components/IdentityVerificationSection";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAvatar } from "@/shared/components/data-display/ColocaAvatar";
import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import { ColocaEmpty } from "@/shared/components/feedback/ColocaEmpty";
import { useAppShellBreadcrumb } from "@/shared/components/layout/useAppShellBreadcrumb";
import { ColocaTooltip } from "@/shared/components/overlays/ColocaTooltip";
import useTranslations from "@/shared/lang/useTranslations";
import { HttpApiService } from "@/shared/services/httpApi.service";
import { parseDateInput } from "@/shared/utils/dateTimeHelpers";
import { colocaToast } from "@/shared/utils/toast";

import { CardsHeader } from "../components/CardsHeader";
import { CreateCardModal } from "../components/CreateCardModal";
import { KycLinkCountdownPill } from "../components/KycLinkCountdownPill";
import { countryName, flagFromCountryCode } from "../constants/countries.constants";
import { kycStatusMeta, resolveIdentityDisplayName } from "../constants/identityTiers.constants";
import { parseExpiry } from "../hooks/useLinkCountdown";
import { CardsService } from "../services/cardsService";
import { IdentitiesService } from "../services/identitiesService";
import type { Card } from "../types/cards-api.types";
import type { Identity } from "../types/identities-api.types";
import { isEligibleHolder } from "../utils/holderEligibility";

const formatUSD = (raw: number): string =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(raw);

const formatDate = (raw?: string | null): string => {
  if (!raw) return "—";
  const d = parseDateInput(raw);
  if (Number.isNaN(d.getTime())) return raw;
  return d.toLocaleDateString("es-ES", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

export const IdentityDetailPage = () => {
  const { t } = useTranslations("cards");
  const { t: tGlobal } = useTranslations("global");
  const navigate = useNavigate();
  const params = useParams<"id">();
  const identityId = params.id!;
  const { can } = useAuth();
  // Driving a verification belongs to whoever is helping the holder through it —
  // their own organization or Coloca staff — so the only gate here is the
  // permission the two endpoints declare (`identities:write`), with no tenancy
  // condition on top. The backend is built for both callers: an unscoped
  // (INTERNAL) issue stamps the link with the identity's owning organization, so
  // its members keep seeing the link staff just sent instead of a "no link"
  // screen that tempts them into regenerating over it.
  const canWriteKyc = can("identities:write");

  const [identity, setIdentity] = useState<Identity | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [identityCards, setIdentityCards] = useState<Card[]>([]);
  const [isLoadingCards, setIsLoadingCards] = useState(false);
  const [isCreateCardOpen, setIsCreateCardOpen] = useState(false);
  const [cardsRefreshKey, setCardsRefreshKey] = useState(0);

  /** The link issued in this session — strictly newer than whatever the detail returned. */
  const [issuedLink, setIssuedLink] = useState<{ url: string; expiresAt: string | null } | null>(null);
  const [copied, setCopied] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  /** Set by the countdown when the link dies with the page open, so we stop offering it. */
  const [hasLinkExpired, setHasLinkExpired] = useState(false);
  const copyResetRef = useRef<number | null>(null);

  const displayName = identity ? resolveIdentityDisplayName(identity) : "";

  const breadcrumb = useMemo(
    () => [
      { label: tGlobal("sidebar_section_tarjetas") },
      { label: tGlobal("sidebar_menu_cards"), to: "/app/cards" },
      { label: t("cards_tab_label_identities"), to: "/app/cards/identities" },
      displayName ? { label: displayName } : { label: "", loading: true },
    ],
    [t, tGlobal, displayName],
  );
  useAppShellBreadcrumb(breadcrumb);

  // The issued link belongs to the identity it was minted for. This route
  // reuses the same component instance when only `:id` changes, so without this
  // reset a link issued for holder A would survive into holder B's page — and
  // "Send by email" would mail B a bearer token that completes A's KYC. No path
  // into this view does that today; the guard is here so adding one later
  // cannot turn a navigation into a credential leak.
  useEffect(() => {
    setIssuedLink(null);
    setHasLinkExpired(false);
    setCopied(false);
  }, [identityId]);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    (async () => {
      try {
        const response = await IdentitiesService.getIdentityById(identityId, {
          withAggregates: true,
        });
        if (cancelled) return;
        setIdentity(response.data);
      } catch (err: unknown) {
        if (cancelled) return;
        setError(HttpApiService.getErrorMessage(err) || t("identity_detail_error_load"));
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [identityId, t]);

  useEffect(() => {
    let cancelled = false;
    setIsLoadingCards(true);
    (async () => {
      try {
        const response = await CardsService.fetchCards({
          identity_id: identityId,
          limit: 20,
          skip: 0,
          with_aggregates: true,
        });
        if (cancelled) return;
        const list = Array.isArray(response.data?.data) ? response.data.data : [];
        setIdentityCards(list);
      } catch {
        if (!cancelled) setIdentityCards([]);
      } finally {
        if (!cancelled) setIsLoadingCards(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [identityId, cardsRefreshKey]);

  const kyc = identity ? kycStatusMeta(identity.kyc_status) : null;
  const isKycVerified = kyc?.group === "approved";

  /**
   * Card issuance needs BOTH axes, and they are independent fields: `kyc_status` on the identity and `status`
   * on the row. This used to gate on the KYC bucket alone, which let a `VERIFIED` + `PENDING_REVIEW` holder
   * through — the ordinary state of someone who finished KYC but whose card-provider registration failed.
   * Reuses the same predicate the create-card picker applies to searched holders, so there is one rule
   * instead of two.
   */
  const canIssueCard = identity ? isEligibleHolder(identity) : false;

  // Only Coloca's own link, never the provider's: `identity.kyc_link` is the
  // KYC provider's hosted URL, which is a third party's domain on screen
  // (rule 12) and, with a TTL of minutes frozen at creation, dead anyway. The
  // backend only returns `active_kyc_link` while it is genuinely usable, so its
  // absence means "there is nothing to hand out" — a normal state, not an error.
  const resolvedLink =
    issuedLink ??
    (identity?.active_kyc_link
      ? { url: identity.active_kyc_link, expiresAt: identity.active_kyc_link_expires_at ?? null }
      : null);

  // A link whose expiry we cannot read is not a link we can hand out, and
  // "cannot read" covers an absent value and an unparseable one alike — the
  // backend applies the same rule server-side, where a row with no usable expiry
  // is never returned.
  //
  // `parseExpiry` is the countdown's own predicate rather than a local one on
  // purpose. Gating this on truthiness while the countdown gates on
  // `Number.isFinite` is exactly how the two drift: an unparseable string would
  // make the pill vanish while the input, Copy and Send by email stayed live —
  // the link presented as fine with no signal at all that it is not.
  const activeLink =
    resolvedLink && parseExpiry(resolvedLink.expiresAt) !== null && resolvedLink.expiresAt
      ? { url: resolvedLink.url, expiresAt: resolvedLink.expiresAt }
      : null;

  const kycLink = activeLink && !hasLinkExpired ? activeLink.url : "";

  const cardsActive = identity?.cards_count ?? identityCards.length;
  const monthSpend = 0;

  const handleBack = useCallback(() => {
    navigate("/app/cards/identities");
  }, [navigate]);

  const handleOpenCard = useCallback((cardId: string) => navigate(`/app/cards/cards/${cardId}`), [navigate]);

  useEffect(
    () => () => {
      if (copyResetRef.current !== null) window.clearTimeout(copyResetRef.current);
    },
    [],
  );

  const handleCopyLink = useCallback(() => {
    if (!kycLink) return;
    navigator.clipboard
      .writeText(kycLink)
      .then(() => {
        setCopied(true);
        if (copyResetRef.current !== null) window.clearTimeout(copyResetRef.current);
        copyResetRef.current = window.setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {});
  }, [kycLink]);

  const handleRegenerate = useCallback(async () => {
    if (!identity) return;
    setIsRegenerating(true);
    try {
      const response = await IdentitiesService.regenerateKycLink(identity.id);
      // The expiry ships in the same response, so the countdown is correct
      // immediately — no refetch of the identity needed.
      setIssuedLink({
        url: response.data.verification_url,
        expiresAt: response.data.expires_at ?? null,
      });
      setHasLinkExpired(false);
      colocaToast.success(t("identity_detail_kyc_toast_regenerate_success"));
    } catch (err) {
      colocaToast.error(
        HttpApiService.getErrorMessage(err) || t("identity_detail_kyc_toast_regenerate_error"),
      );
    } finally {
      setIsRegenerating(false);
    }
  }, [identity, t]);

  const handleSendEmail = useCallback(async () => {
    const to = identity?.email?.trim();
    if (!to || !kycLink) return;
    setIsSendingEmail(true);
    try {
      await IdentitiesService.sendKycVerificationMail({
        type: "OPERATIONS_KYC_VERIFICATION",
        to,
        payload: { name: displayName, verificationUrl: kycLink },
      });
      colocaToast.success(t("identity_detail_kyc_toast_email_success"));
    } catch (err) {
      colocaToast.error(HttpApiService.getErrorMessage(err) || t("identity_detail_kyc_toast_email_error"));
    } finally {
      setIsSendingEmail(false);
    }
  }, [identity, kycLink, displayName, t]);

  return (
    <div className="mx-auto w-full max-w-[1200px] space-y-6 p-2">
      <CardsHeader activeTabId="identities" />

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={handleBack}
          className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label={t("identity_detail_back_to_list")}
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          {t("cards_tab_label_identities")}
        </button>
      </div>

      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <span className="text-sm text-muted-foreground">{t("identity_detail_loading")}</span>
          </div>
        </div>
      )}

      {error && !isLoading && (
        <div className="rounded-2xl border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
          {error}
        </div>
      )}

      {identity && !isLoading && (
        <>
          <section className="rounded-2xl border border-border bg-card p-6">
            <div className="flex items-start gap-5">
              <ColocaAvatar
                name={displayName}
                size="lg"
                badge={isKycVerified ? <Check className="size-3" /> : undefined}
              />
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="truncate text-2xl font-semibold text-foreground">{displayName}</h2>
                  {kyc && <ColocaBadge variant={kyc.tone}>{t(kyc.labelKey as never)}</ColocaBadge>}
                </div>
                <div className="mt-4 grid grid-cols-2 gap-4 text-sm md:grid-cols-4">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                      {t("identity_detail_label_document_number")}
                    </div>
                    <div className="font-mono text-foreground">{identity.identification_number || "—"}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                      {t("identity_detail_label_birthdate")}
                    </div>
                    <div className="text-foreground">{formatDate(identity.birthdate)}</div>
                  </div>
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                      {t("identity_detail_label_email")}
                    </div>
                    <div className="truncate text-foreground">{identity.email || "—"}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                      {t("identity_detail_label_phone")}
                    </div>
                    <div className="font-mono text-foreground">{identity.phone || "—"}</div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {!isKycVerified && (
            <section className="rounded-2xl border border-border bg-card p-6">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="text-sm font-semibold text-foreground">
                    {t("identity_detail_kyc_section_title")}
                  </h3>
                  <p className="mt-1 mb-4 text-xs text-muted-foreground">
                    {t("identity_detail_kyc_section_description")}
                  </p>
                </div>
                {activeLink && (
                  <KycLinkCountdownPill
                    expiresAt={activeLink.expiresAt}
                    onExpire={() => setHasLinkExpired(true)}
                  />
                )}
              </div>
              {kycLink ? (
                <div className="flex flex-col gap-2 sm:flex-row">
                  <input
                    type="text"
                    readOnly
                    value={kycLink}
                    placeholder={t("identity_detail_kyc_link_placeholder")}
                    className="min-w-0 flex-1 rounded-lg border border-border bg-muted/40 px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                  <ColocaButton variant="default" icon={Copy} onClick={handleCopyLink}>
                    {copied ? t("identity_detail_kyc_btn_copied") : t("identity_detail_kyc_btn_copy")}
                  </ColocaButton>
                </div>
              ) : (
                // No empty input pretending to be a broken field: with no live
                // link there is nothing to copy, so say so and offer the way out.
                <p className="rounded-lg border border-dashed border-border bg-muted/30 px-3 py-2.5 text-xs text-muted-foreground">
                  {t("identity_detail_kyc_link_empty")}
                </p>
              )}
              {canWriteKyc && (
                <>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <ColocaButton
                      variant={kycLink ? "secondary" : "primary"}
                      icon={kycLink ? RefreshCw : Link2}
                      onClick={handleRegenerate}
                      loading={isRegenerating}
                      disabled={isSendingEmail}
                    >
                      {kycLink
                        ? t("identity_detail_kyc_btn_regenerate")
                        : t("identity_detail_kyc_btn_generate")}
                    </ColocaButton>
                    <ColocaButton
                      variant="primary"
                      icon={Mail}
                      onClick={handleSendEmail}
                      loading={isSendingEmail}
                      // Blocked while a new link is in flight: otherwise this would
                      // mail the old URL, which the pending issue is about to kill.
                      disabled={isRegenerating || !kycLink || !identity.email}
                    >
                      {t("identity_detail_kyc_btn_send_email")}
                    </ColocaButton>
                  </div>
                  {!identity.email && (
                    // The recipient is always the holder's own contact on record,
                    // never the organization's inbox — so with no email there is
                    // nobody to send to, and the link still has to reach them by
                    // some other channel. Saying that beats a disabled control
                    // with no reason, which reads as broken.
                    <p className="mt-2 text-xs text-muted-foreground">
                      {t("identity_detail_kyc_no_recipient")}
                    </p>
                  )}
                </>
              )}
            </section>
          )}

          <section className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="rounded-2xl border border-border bg-card p-4">
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
                {t("identity_detail_stats_cards_active")}
              </div>
              <div className="mt-1 text-2xl font-semibold text-foreground">{cardsActive}</div>
            </div>
            <div className="rounded-2xl border border-border bg-card p-4">
              <div className="flex items-center justify-between gap-2">
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
                  {t("identity_detail_stats_month_spend")}
                </div>
                <ColocaBadge variant="secondary">{t("identity_detail_coming_soon")}</ColocaBadge>
              </div>
              <div className="mt-1 text-2xl font-semibold text-foreground">{formatUSD(monthSpend)}</div>
            </div>
          </section>

          <section className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div className="overflow-hidden rounded-2xl border border-border bg-card">
              <div className="flex items-center justify-between border-b border-border p-4">
                <h3 className="text-sm font-semibold text-foreground">
                  {t("identity_detail_cards_section_title")}
                </h3>
                {canIssueCard ? (
                  <ColocaButton
                    variant="default"
                    size="sm"
                    icon={Plus}
                    onClick={() => setIsCreateCardOpen(true)}
                  >
                    {t("card_new_button")}
                  </ColocaButton>
                ) : (
                  <ColocaTooltip
                    trigger={
                      <ColocaButton variant="default" size="sm" icon={Plus} disabled>
                        {t("card_new_button")}
                      </ColocaButton>
                    }
                  >
                    {t("card_new_requires_eligible_holder")}
                  </ColocaTooltip>
                )}
              </div>
              {isLoadingCards ? (
                <div className="flex items-center justify-center p-8">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              ) : identityCards.length === 0 ? (
                <div className="p-8 text-center text-sm text-muted-foreground">
                  {t("identity_detail_cards_empty")}
                </div>
              ) : (
                <ul className="divide-y divide-border">
                  {identityCards.map((card) => {
                    const last4 = card.last4 || card.last_4 || "0000";
                    const alias = card.alias?.trim() || `•••• ${last4}`;
                    const monthSpendCard =
                      typeof card.month_spend_usd === "number" ? card.month_spend_usd : 0;
                    return (
                      <li key={card.id}>
                        <button
                          type="button"
                          onClick={() => handleOpenCard(card.id)}
                          className="flex w-full items-center gap-3 p-3.5 text-left transition-colors hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
                        >
                          <div
                            className="relative h-9 w-14 shrink-0 overflow-hidden rounded text-white"
                            style={{
                              background:
                                "linear-gradient(135deg, var(--color-brand-700), var(--color-brand-950))",
                            }}
                          >
                            <div className="absolute inset-0 flex items-end p-1.5 font-mono text-[8px] text-white/90">
                              ··{last4}
                            </div>
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="truncate text-sm font-medium text-foreground">{alias}</div>
                            <div className="text-[11px] text-muted-foreground">
                              {t("cards_label_month_spend")}: {formatUSD(monthSpendCard)}
                            </div>
                          </div>
                          <ChevronRight
                            className="h-4 w-4 shrink-0 text-muted-foreground"
                            aria-hidden="true"
                          />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <div className="overflow-hidden rounded-2xl border border-border bg-card">
              <div className="border-b border-border p-4">
                <h3 className="text-sm font-semibold text-foreground">
                  {t("identity_detail_movements_section_title")}
                </h3>
              </div>
              <div className="p-6">
                <ColocaEmpty
                  title={t("identity_detail_movements_coming_soon_title")}
                  description={t("identity_detail_movements_coming_soon_description")}
                />
              </div>
            </div>
          </section>

          {/* Parked: INTERNAL-only, and both of its actions are blocked for
              INTERNAL, so today it serves as the documents viewer and nothing
              else. Organization members do not see it at all. */}
          <InternalOnly>
            <IdentityVerificationSection identity={identity} />
          </InternalOnly>

          {identity.address && (
            <section className="rounded-2xl border border-border bg-card p-6">
              <h3 className="mb-3 text-sm font-semibold text-foreground">
                {t("identity_detail_section_address_title")}
              </h3>
              <div className="grid grid-cols-1 gap-3 text-sm md:grid-cols-2 lg:grid-cols-4">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                    {t("identity_detail_label_address_line_1")}
                  </div>
                  <div className="text-foreground">{identity.address.address_line_1 || "—"}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                    {t("identity_detail_label_city")}
                  </div>
                  <div className="text-foreground">{identity.address.city || "—"}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                    {t("identity_detail_label_country")}
                  </div>
                  <div className="flex items-center gap-1.5 text-foreground">
                    {identity.address.country && (
                      <span aria-hidden="true" className="text-base leading-none">
                        {flagFromCountryCode(identity.address.country)}
                      </span>
                    )}
                    <span>{identity.address.country ? countryName(identity.address.country) : "—"}</span>
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                    {t("identity_detail_label_created_at")}
                  </div>
                  <div className="text-foreground">{formatDate(identity.created_at)}</div>
                </div>
              </div>
            </section>
          )}
        </>
      )}

      <CreateCardModal
        open={isCreateCardOpen}
        onOpenChange={setIsCreateCardOpen}
        presetHolderId={identityId}
        onCreated={() => setCardsRefreshKey((k) => k + 1)}
      />
    </div>
  );
};
